import React, {Component} from 'react';
import PropTypes from 'prop-types';
import {Query, Builder, Utils as QbUtils} from '@react-awesome-query-builder/ui';
import {MuiConfig} from '@react-awesome-query-builder/mui';
import {createTheme, ThemeProvider} from '@mui/material/styles';
import {enUS, deDE} from '@mui/material/locale';
import 'moment/locale/de';
import * as R from 'ramda';
import '@react-awesome-query-builder/ui/css/styles.css';

// Minimal base config. You can extend this in Python.
const InitialConfig = MuiConfig;

/**
 * QueryBuilder is a Dash component that wraps the React Awesome Query Builder.
 * 
 * It allows users to construct complex queries visually and outputs them
 * in various formats (SQL, JsonLogic, SpEL).
 */
export default class QueryBuilder extends Component {
    constructor(props) {
        super(props);

        const config = this.createConfig(props);
        // Load initial tree or create empty
        const emptyInitValue = {id: QbUtils.uuid(), type: "group"};
        const initialData = props.tree || props.value;
        const tree = initialData 
            ? QbUtils.checkTree(QbUtils.loadTree(initialData), config)
            : QbUtils.checkTree(QbUtils.loadTree(emptyInitValue), config);

        this.state = {
            config: config,
            tree: tree
        };
    }

    applyLocalization(config, translations) {
        if (translations) {
            return R.mergeDeepRight(config, translations);
        }
        return config;
    }

    createConfig(props) {
        const localeInput = props.locale || {};
        
        let muiLocale = enUS;
        if (localeInput.mui && typeof localeInput.mui === 'string' && localeInput.mui.toLowerCase().indexOf('de') !== -1) {
            muiLocale = deDE;
        }

        const localeSettings = {
            ...InitialConfig.settings.locale,
            ...localeInput,
            mui: muiLocale
        };

        const settings = {
            ...InitialConfig.settings,
            liteMode: props.uiMode === 'lite',
            themeMode: props.themeMode || 'light',
            locale: localeSettings
        };

        let themeOptions = props.theme ? props.theme : (InitialConfig.theme || {});
        if (props.theme && props.theme.mui) {
            themeOptions = props.theme.mui;
        }
        const themeConfig = createTheme(themeOptions, muiLocale);
        
        let config = {
            ...InitialConfig,
            settings,
            fields: props.fields || {},
            theme: themeConfig,
            locale: localeSettings
        };

        config = this.applyLocalization(config, props.translations);
        
        return config;
    }

    componentDidMount() {
        const {setProps} = this.props;
        if (setProps) {
            const {tree, config} = this.state;
            const jsonTree = QbUtils.getTree(tree);
            const jsonLogic = QbUtils.jsonLogicFormat(tree, config);

            setProps({
                value: jsonTree,
                tree: jsonTree,
                sql: QbUtils.sqlFormat(tree, config),
                jsonLogic: jsonLogic,
                jsonLogicFormat: jsonLogic,
                spel: QbUtils.spelFormat(tree, config)
            });
        }
    }

    componentDidUpdate(prevProps) {
        let newState = {};
        let config = this.state.config;

        if (prevProps.uiMode !== this.props.uiMode || 
            prevProps.themeMode !== this.props.themeMode ||
            JSON.stringify(prevProps.locale) !== JSON.stringify(this.props.locale) ||
            JSON.stringify(prevProps.theme) !== JSON.stringify(this.props.theme) ||
            JSON.stringify(prevProps.fields) !== JSON.stringify(this.props.fields) ||
            JSON.stringify(prevProps.translations) !== JSON.stringify(this.props.translations)) {
            
            config = this.createConfig(this.props);
            newState.config = config;
        }

        if (this.props.tree && this.props.tree !== prevProps.tree) {
            const currentTreeJson = QbUtils.getTree(this.state.tree);
            // Compare serialized JSON to avoid loops if the update originated from this component
            if (JSON.stringify(this.props.tree) !== JSON.stringify(currentTreeJson)) {
                const newTree = QbUtils.checkTree(QbUtils.loadTree(this.props.tree), config);
                newState.tree = newTree;
            }
        }

        if (Object.keys(newState).length > 0) {
            this.setState(newState);
        }
    }

    onChange = (immutableTree, config) => {
        const {setProps} = this.props;
        const configToUse = this.state.config;
        const jsonTree = QbUtils.getTree(immutableTree);
        const jsonLogic = QbUtils.jsonLogicFormat(immutableTree, configToUse);
        
        // Minimal overhead: Calculate common formats on the fly
        const results = {
            value: jsonTree,
            tree: jsonTree,
            sql: QbUtils.sqlFormat(immutableTree, configToUse),
            jsonLogic: jsonLogic,
            jsonLogicFormat: jsonLogic,
            spel: QbUtils.spelFormat(immutableTree, configToUse)
        };

        this.setState({tree: immutableTree});

        if (setProps) {
            setProps(results);
        }
    };

    render() {
        return (
            <div className="query-builder-container">
                <ThemeProvider theme={this.state.config.theme}>
                    <Query
                        {...this.state.config}
                        value={this.state.tree}
                        onChange={this.onChange}
                        renderBuilder={(props) => <div className="query-builder"><Builder {...props} /></div>}
                    />
                </ThemeProvider>
            </div>
        );
    }
}

QueryBuilder.propTypes = {
    /**
     * The ID used to identify this component in Dash callbacks.
     */
    id: PropTypes.string,

    /**
     * The mode of the UI. 'full' enables all features (grouping, reordering).
     * 'lite' restricts the UI to a simpler interface (no groups, no reordering).
     * Default is 'full'.
     */
    uiMode: PropTypes.oneOf(['full', 'lite']),

    /**
     * The theme mode. 'light' or 'dark'.
     */
    themeMode: PropTypes.oneOf(['light', 'dark']),

    /**
     * The locale configuration object.
     * Allows overriding localization settings.
     * Can include parameters for locale.moment and locale.mui.
     */
    locale: PropTypes.object,

    /**
     * The field configuration for the query builder.
     * This defines the available fields, their types, and options.
     * It should follow the React Awesome Query Builder configuration format.
     */
    fields: PropTypes.object.isRequired,

    /**
     * The theme configuration.
     * Allows overriding the MUI theme settings (e.g., palette).
     * Example: { mui: { palette: { primary: { main: '#ff0000' } } } }
     */
    theme: PropTypes.object,

    /**
     * A dictionary of translations to apply to the query builder configuration.
     * This allows overriding labels for conjunctions, operators, settings, etc.
     */
    translations: PropTypes.object,

    /**
     * The current value of the query tree.
     * Can be used to set the initial state of the builder.
     */
    value: PropTypes.object,

    /**
     * The current value of the query tree in JSON format.
     * This is a read-only property updated by the component.
     */
    tree: PropTypes.object,

    /**
     * The generated SQL string representing the current query.
     * This is a read-only property updated by the component.
     */
    sql: PropTypes.string,

    /**
     * The generated JsonLogic object representing the current query.
     * This is a read-only property updated by the component.
     */
    jsonLogic: PropTypes.object,

    /**
     * The generated JsonLogic object representing the current query.
     * This is a read-only property updated by the component.
     */
    jsonLogicFormat: PropTypes.object,

    /**
     * The generated SpEL string representing the current query.
     * This is a read-only property updated by the component.
     */
    spel: PropTypes.string,

    /**
     * Dash-assigned callback that should be called to report property changes
     * to Dash, to make them available for callbacks.
     */
    setProps: PropTypes.func
};